<template>
  <div>
    <ClinicPageHeader title="Agenda geral" :subtitle="headerSubtitle">
      <template #actions>
        <router-link
          :to="{ name: 'ClinicConfirmations' }"
          class="btn btn--outline btn--sm"
        >
          Confirmações ({{ confirmacoesCount }})
        </router-link>
        <button
          v-if="canEdit('agenda')"
          class="btn btn--primary btn--sm"
          @click="showNew = true"
        >
          + Novo agendamento
        </button>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar a agenda"
        message="Ocorreu um erro ao buscar as consultas da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="agenda-head">
          <div class="skel-line" style="width:280px;height:36px" />
          <div class="skel-line" style="width:320px;height:42px" />
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:420px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <div class="agenda-head">
          <div class="agenda-nav">
            <button type="button" @click="prevDay">‹</button>
            <span class="today">{{ currentLabel }}</span>
            <button type="button" @click="nextDay">›</button>
            <button
              type="button"
              class="btn btn--ghost btn--sm"
              style="margin-left:8px"
              @click="goToday"
            >
              Hoje
            </button>
          </div>
          <div class="row" style="gap:var(--space-3)">
            <select
              v-model="specialtyFilter"
              class="select"
              style="width:auto;height:42px"
              @change="onSpecialtyChange"
            >
              <option value="">Todas as especialidades</option>
              <option v-for="sp in specialties" :key="sp.id" :value="sp.name">
                {{ sp.name }}
              </option>
            </select>
            <button
              v-if="canEdit('agenda')"
              class="btn btn--primary btn--sm"
              @click="showNew = true"
            >
              + Novo agendamento
            </button>
          </div>
        </div>

        <!-- Dia vazio -->
        <ClinicStateBox
          v-if="!hasAppointments"
          state="empty"
          title="Nenhuma consulta neste dia"
          message="Não há consultas agendadas para os profissionais de saúde selecionados neste dia."
        />

        <template v-else>
          <div class="agenda" :style="{ '--cols': columns.length }">
            <div class="col-h corner" />
            <div v-for="doc in columns" :key="doc.id" class="col-h">
              <div class="avatar avatar--sm" :class="doc.avatarClass">
                {{ doc.initials }}
              </div>
              <div class="stack" style="gap:0;min-width:0">
                <span class="nm">{{ doc.name }}</span>
                <span class="sp">{{ doc.specialtyShort }}</span>
              </div>
            </div>

            <template v-for="row in grid">
              <div :key="`t-${row.time}`" class="time">{{ row.time }}</div>
              <div
                v-for="(cell, ci) in row.cells"
                :key="`c-${row.time}-${ci}`"
                class="cell"
              >
                <div
                  v-if="cell"
                  class="appt-block is-clickable"
                  :class="`is-${cell.status}`"
                  @click="openDetail(cell, row.time)"
                >
                  <b>{{ cell.patient }}</b>
                  <span>{{ cell.type }}</span>
                </div>
              </div>
            </template>
          </div>

          <div class="legend">
            <div v-for="lg in legend" :key="lg.label" class="lg">
              <i :style="{ background: lg.color }" />
              {{ lg.label }}
            </div>
          </div>
        </template>
      </template>
    </div>

    <ClinicNewAppointment
      v-if="showNew"
      @close="showNew = false"
      @created="onCreated"
    />
    <ClinicAppointmentDetail
      v-if="selectedAppt"
      :appt="selectedAppt"
      @close="selectedAppt = null"
    />
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicNewAppointment from "@/components/clinica/ClinicNewAppointment.vue";
import ClinicAppointmentDetail from "@/components/clinica/ClinicAppointmentDetail.vue";
import clinicaListQuery from "@/mixins/clinicaListQuery";
import { ClinicAgenda, ClinicConfig } from "@/services/clinica";

// Mapeia o status da consulta (backend) para a classe visual do bloco de agenda.
const STATUS_CLASS = {
  confirmada: "confirmed",
  agendada: "pending",
  aguardando_pgto: "pending",
  realizada: "done",
  cancelada: "blocked",
  bloqueio: "blocked"
};

export default {
  name: "ClinicSchedule",
  components: {
    ClinicPageHeader,
    ClinicStateBox,
    ClinicNewAppointment,
    ClinicAppointmentDetail
  },
  mixins: [clinicaListQuery, clinicCan],
  data() {
    return {
      state: "loading",
      // Modal de novo agendamento (abre também via ?novo=1, ex.: CTA do dashboard)
      showNew: false,
      // Consulta selecionada na grade (modal de detalhe)
      selectedAppt: null,
      // Especialidades reais (GET /clinica/specialties) — não fixture.
      specialties: [],
      // Dados da grade vindos do service (colunas = médicos, linhas = horários).
      columns: [],
      rows: [],
      appointments: [],
      specialtyFilter: "",
      // Dia exibido na agenda (navegável via ‹ › / Hoje) — default: hoje.
      currentDate: new Date(),
      // Só os status que a agenda emite hoje (confirmed|pending). "Realizada"
      // e "Bloqueio" saem da legenda até existirem na grade (ver DT-3).
      legend: [
        { label: "Confirmada", color: "var(--color-primary)" },
        { label: "A confirmar", color: "var(--color-warning)" }
      ]
    };
  },
  created() {
    const q = this.clinicaReadQuery({ specialty: "" });
    this.specialtyFilter = q.specialty || "";
    if (this.$route.query.novo && this.canEdit("agenda")) this.showNew = true;
    this.fetch();
    this.fetchSpecialties();
  },
  methods: {
    // Consulta criada pelo modal → fecha e recarrega a grade do dia.
    onCreated() {
      this.showNew = false;
      this.fetch();
    },
    async fetchSpecialties() {
      try {
        const { data } = await ClinicConfig.specialties();
        this.specialties = data || [];
      } catch (e) {
        this.specialties = [];
      }
    },
    async fetch() {
      this.state = "loading";
      try {
        const { data } = await ClinicAgenda.day({
          unitId: this.selectedUnitId,
          date: this.dateParam,
          specialty: this.specialtyFilter || undefined,
          fail: this.$route.query.state === "error"
        });
        this.columns = data.columns;
        this.rows = data.rows;
        this.appointments = data.appointments;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    // Escreve o filtro na URL; o watch de $route.query dispara o refetch.
    onSpecialtyChange() {
      this.clinicaSyncQuery({ specialty: this.specialtyFilter });
    },
    // Célula da grade: consulta do médico `doctorId` no horário `time` (ou null).
    cellFor(time, doctorId) {
      const ap = this.appointments.find(
        a => a.doctorId === doctorId && a.time === time
      );
      if (!ap) return null;
      return {
        id: ap.id,
        patient: ap.patientName,
        doctorName: ap.doctorName,
        type: ap.type,
        rawStatus: ap.status,
        status: STATUS_CLASS[ap.status] || "pending"
      };
    },
    // Clique num bloco da grade → modal de detalhe da consulta.
    openDetail(cell, time) {
      this.selectedAppt = {
        id: cell.id,
        patient: cell.patient,
        doctor: cell.doctorName,
        time,
        dateLabel: this.currentLabel,
        status: cell.rawStatus
      };
    },
    prevDay() {
      this.shiftDay(-1);
    },
    nextDay() {
      this.shiftDay(1);
    },
    goToday() {
      this.currentDate = new Date();
      this.fetch();
    },
    shiftDay(delta) {
      const d = new Date(this.currentDate);
      d.setDate(d.getDate() + delta);
      this.currentDate = d;
      this.fetch();
    }
  },
  computed: {
    // Contagem REAL de consultas do dia exibido (antes era "14" fixo do proto).
    headerSubtitle() {
      const n = this.appointments.length;
      const label = n === 1 ? "consulta" : "consultas";
      return `Visão consolidada da clínica · ${n} ${label} no dia`;
    },
    // "Sexta, 19 de junho de 2026" — data REAL exibida (antes era fixa).
    currentLabel() {
      const d = this.currentDate;
      const weekday = d
        .toLocaleDateString("pt-BR", { weekday: "long" })
        .replace("-feira", "");
      const rest = d.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${rest}`;
    },
    // YYYY-MM-DD local (sem UTC shift) p/ a query da API.
    dateParam() {
      const d = this.currentDate;
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${m}-${day}`;
    },
    // Contexto de unidade ativo (Vuex) — dispara refetch da agenda ao trocar.
    selectedUnitId() {
      return this.$store.getters["clinica/selectedUnitId"];
    },
    confirmacoesCount() {
      const badges = this.$store.getters["clinica/badges"];
      return (badges && badges.confirmacoes) || 0;
    },
    // Grade renderizável: cada linha (horário) alinha 1:1 com as colunas de médicos.
    grid() {
      return this.rows.map(time => ({
        time,
        cells: this.columns.map(doc => this.cellFor(time, doc.id))
      }));
    },
    hasAppointments() {
      if (!this.columns.length || !this.rows.length) return false;
      const colIds = this.columns.map(c => c.id);
      return this.appointments.some(a => colIds.includes(a.doctorId));
    }
  },
  watch: {
    // Trocar de unidade refaz o fetch da agenda.
    selectedUnitId() {
      this.fetch();
    },
    // Reage a voltar/avançar do browser e à sincronização do filtro na URL.
    "$route.query"() {
      const q = this.clinicaReadQuery({ specialty: "" });
      this.specialtyFilter = q.specialty || "";
      this.fetch();
    }
  }
};
</script>
